"use client";

import { createContext, useContext, useMemo } from "react";
import { usePathname, useRouter } from "next/navigation";
import { DEFAULT_LOCALE, type Locale } from "./config";
import { getMessages, type Messages } from "./messages";
import { localizePath, splitLocale } from "./paths";

// El idioma lo fija app/[lang]/layout.tsx a partir de la URL; los componentes
// de cliente lo leen de acá (los de servidor lo reciben por params/prop).
const LocaleContext = createContext<Locale>(DEFAULT_LOCALE);

export function LocaleProvider({ lang, children }: { lang: Locale; children: React.ReactNode }) {
  return <LocaleContext.Provider value={lang}>{children}</LocaleContext.Provider>;
}

export function useLocale(): Locale {
  return useContext(LocaleContext);
}

export function useMessages<T>(messages: Messages<T>): T {
  return getMessages(messages, useLocale());
}

// "/eventos" → "/en/eventos" según el idioma actual.
export function useLocalize(): (path: string) => string {
  const lang = useLocale();
  return useMemo(() => (path: string) => localizePath(path, lang), [lang]);
}

// Igual que useRouter de next/navigation, pero push/replace/prefetch agregan
// el prefijo de idioma a las rutas internas ("/eventos" → "/es/eventos").
export function useLocalizedRouter(): ReturnType<typeof useRouter> {
  const router = useRouter();
  const lang = useLocale();
  return useMemo(
    () => ({
      ...router,
      push: (href, options) => router.push(localizePath(href, lang), options),
      replace: (href, options) => router.replace(localizePath(href, lang), options),
      prefetch: (href, options) => router.prefetch(localizePath(href, lang), options),
    }),
    [router, lang],
  );
}

// Pathname SIN prefijo de idioma ("/en/eventos" → "/eventos"), para comparar
// contra rutas (nav activa, etc.) igual que antes del i18n.
export function useLocalizedPathname(): string {
  return splitLocale(usePathname() ?? "/").path;
}
